import { useEffect } from 'react';
import {
  MANAGED_ATTRIBUTE as MANAGED,
  type AlternateLink,
  type MetaTag,
  type PageMetadata,
} from '@/lib/seo/metadata';

const ALTERNATE = 'data-seo-alternate';

type Restore = () => void;

/**
 * Drops every element a previous pass left behind — including the ones baked
 * into a prerendered snapshot, which hydration would otherwise mistake for
 * permanent shell tags. What remains is exactly the singletons index.html
 * ships, so page-specific values cannot outlive their page and hreflang links
 * cannot accumulate.
 */
function clearManaged(): void {
  document.head.querySelectorAll(`[${MANAGED}], [${ALTERNATE}]`).forEach((element) => element.remove());
}

function createManaged(tagName: 'meta' | 'link', attributes: Record<string, string>): Restore {
  const element = document.createElement(tagName);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  element.setAttribute(MANAGED, 'true');
  document.head.appendChild(element);
  return () => element.remove();
}

/** Updates a tag index.html owns, restoring its shipped value on cleanup. */
function updateShell(element: Element, name: string, value: string): Restore {
  const previous = element.getAttribute(name);
  element.setAttribute(name, value);
  return () => {
    if (previous === null) element.removeAttribute(name);
    else element.setAttribute(name, previous);
  };
}

interface MetaGroup {
  key: MetaTag['key'];
  value: string;
  contents: string[];
}

/**
 * Collects tags by identity, preserving first-appearance order. Repeatable
 * properties such as `og:locale:alternate` legitimately occur more than once,
 * so they are reconciled as a group rather than one `querySelector` per entry —
 * which would make each occurrence overwrite the last.
 */
function groupTags(meta: MetaTag[]): MetaGroup[] {
  const groups = new Map<string, MetaGroup>();

  for (const tag of meta) {
    const identity = `${tag.key}=${tag.value}`;
    const group = groups.get(identity);
    if (group) group.contents.push(tag.content);
    else groups.set(identity, { key: tag.key, value: tag.value, contents: [tag.content] });
  }

  return [...groups.values()];
}

function applyMetaGroup(group: MetaGroup): Restore {
  // `:not([MANAGED])` so a leftover from this hook is never mistaken for a
  // shell tag, even if the sweep above was somehow bypassed.
  const shell = document.head.querySelector(
    `meta[${group.key}="${CSS.escape(group.value)}"]:not([${MANAGED}])`,
  );

  const restores = group.contents.map((content, index) =>
    index === 0 && shell
      ? updateShell(shell, 'content', content)
      : createManaged('meta', { [group.key]: group.value, content }),
  );

  return () => restores.forEach((undo) => undo());
}

function applyCanonical(href?: string): Restore {
  const shell = document.head.querySelector(`link[rel="canonical"]:not([${MANAGED}])`);
  if (href) {
    return shell ? updateShell(shell, 'href', href) : createManaged('link', { rel: 'canonical', href });
  }

  // The not-found page intentionally has no canonical. Remove the canonical
  // index.html ships instead of leaving the root URL in the 404 snapshot, then
  // restore it when the route unmounts so the next page can reconcile it.
  if (!shell) return () => {};

  const nextSibling = shell.nextSibling;
  shell.remove();
  return () => {
    if (nextSibling?.parentNode === document.head) document.head.insertBefore(shell, nextSibling);
    else document.head.appendChild(shell);
  };
}

function applyAlternates(alternates: AlternateLink[]): Restore {
  const restores = alternates.map(({ hreflang, href }) =>
    createManaged('link', { rel: 'alternate', hreflang, href, [ALTERNATE]: 'true' }),
  );
  return () => restores.forEach((undo) => undo());
}

/**
 * Publishes a route's metadata into `<head>` — title, description, canonical,
 * hreflang alternates, Open Graph and Twitter tags — and undoes it on unmount.
 *
 * Pass the object from `pageMetadata()`: it is the single generator for every
 * public page, and it is cached per route so this effect does not re-run on
 * every render.
 */
export function usePageSEO(metadata: PageMetadata) {
  useEffect(() => {
    clearManaged();

    const restore: Restore[] = [];

    const previousTitle = document.title;
    document.title = metadata.title;
    restore.push(() => {
      document.title = previousTitle;
    });

    for (const group of groupTags(metadata.meta)) restore.push(applyMetaGroup(group));
    restore.push(applyCanonical(metadata.canonical));
    restore.push(applyAlternates(metadata.alternates));

    return () => {
      for (const undo of restore.reverse()) undo();
    };
  }, [metadata]);
}
